import * as React from 'react';
import {
  CitationGrid,
  PanelSection,
  SidePanel,
  WorkspaceButton,
  type CitationGridItem,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip } from '@/components/ui/citation-chip';
import {
  metadataEntries,
  useItemUsageQuery,
  useModuleContentQuery,
  useModuleVersionsQuery,
  type LibraryModuleSummary,
} from '@/lib/api/requirement-library';
import { aiDocumentSource } from '@/lib/api/requirement-library-intake';
import { useUsersLookupQuery } from '@/lib/api/users-lookup';
import { formatDate, matchKindWords, plural, type LibraryRow } from './library-logic';
import {
  citationLocation,
  documentLabel,
  locationChip,
  locationWords,
  NO_PAGES_NOTE,
} from './intake/intake-logic';
import type { SourceTarget } from './intake/intake-source-viewer';

/**
 * Side panel for one library requirement (T-0141, E11-F01 scenario 3).
 *
 * Reference: `libPanel()` in `docs/requirement-library/prototype/
 * selection.js`. Shows the full text, where the requirement sits, where
 * it came from (amendment 01 Decision 33), the module version it belongs
 * to, how many engagements loaded it (a count only) and the module's
 * version history.
 *
 * The library is neutral (Decision 31): there is no criticality here and
 * no action that adds the requirement to an engagement.
 *
 * In edit mode the text becomes editable, a look-alike flag offers
 * "Keep both" or a way to discard the curator's own change, and the
 * footer retires the requirement or undoes a pending retire.
 */

const BOX = 'rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700';
const DIFF_BOX =
  'rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-accent-amber-50 px-3 py-2.5 text-body-sm text-ink-700';

export interface LibraryPanelProps {
  row: LibraryRow;
  module: LibraryModuleSummary | null;
  /** True when this row's module is being edited by a curator. */
  editing: boolean;
  /** A draft write is in flight. */
  busy: boolean;
  onClose: () => void;
  onCommitText: (row: LibraryRow, text: string) => void;
  onRetire: (row: LibraryRow) => void;
  onRestore: (row: LibraryRow) => void;
  onKeepBoth: (row: LibraryRow) => void;
  onDiscardDuplicate: (row: LibraryRow) => void;
  /** Open the version history: one version, or the comparison view. */
  onOpenHistory: (moduleId: string, version: number | 'compare') => void;
  /**
   * Open the passage an AI-extracted requirement came from. Curators only:
   * the source document routes need the curation authority.
   */
  onOpenSource?: (target: SourceTarget) => void;
}

function sourceItems(row: LibraryRow): { items: CitationGridItem[]; note: string } | null {
  switch (row.source_kind) {
    case 'caliber_v1':
      return {
        items: [
          { label: 'Source', value: 'Caliber v1' },
          ...(row.v1_number ? [{ label: 'v1 number', value: row.v1_number, mono: true }] : []),
        ],
        note: row.v1_number
          ? `Carried over from the Caliber v1 catalog, where it was numbered ${row.v1_number}.`
          : 'Carried over from the Caliber v1 catalog.',
      };
    case 'spreadsheet':
      return {
        items: [{ label: 'Source', value: 'Spreadsheet import' }],
        note: 'Brought into the library from an imported spreadsheet.',
      };
    default:
      return null;
  }
}

/**
 * Where an AI-extracted requirement came from (amendment 01 Decision 33):
 * the document, the page, the model's confidence, and who accepted it.
 * Shown on the AI surface tone with the shared chips, because it is
 * AI-derived provenance. The citation is written once at confirm and never
 * changes; a draft row carries it, a published row gets it from its module.
 */
function AiDocumentSource({
  row,
  module,
  onOpenSource,
}: {
  row: LibraryRow;
  module: LibraryModuleSummary | null;
  onOpenSource?: ((target: SourceTarget) => void) | undefined;
}) {
  const needsContent = row.source_ref == null && row.version != null && module != null;
  const contentQuery = useModuleContentQuery(
    needsContent ? row.module_id : null,
    needsContent ? module.current_version : null,
  );
  const sourceRef =
    row.source_ref ?? contentQuery.data?.items.find((i) => i.item_id === row.item_id)?.source_ref;
  const ref = aiDocumentSource(sourceRef);
  const names = useUsersLookupQuery(ref?.accepted_by ? [ref.accepted_by] : []).data?.items;
  const acceptedBy = ref?.accepted_by
    ? (names?.find((u) => u.id === ref.accepted_by)?.display_name ?? 'a curator')
    : 'a curator';

  if (!ref) {
    return needsContent && contentQuery.isPending ? (
      <Spinner />
    ) : (
      <>
        <CitationGrid tone="ai" items={[{ label: 'Source', value: 'Source document' }]} />
        <p className="mt-1.5 text-meta text-ink-500">
          Extracted from a document by AI and accepted by a curator.
        </p>
      </>
    );
  }

  const pages = ref.pages ?? [];
  const target =
    ref.run_id && ref.candidate_id ? { runId: ref.run_id, candidateId: ref.candidate_id } : null;
  // T-0155: where each passage is. A requirement accepted before locations
  // were recorded is read by its file type: a Word file then had no pages
  // and a workbook one page per sheet.
  const fileName = ref.file_name ?? '';
  const pageSource =
    ref.page_source ??
    (/\.docx$/i.test(fileName) ? 'none' : /\.xlsx?$/i.test(fileName) ? 'sheets' : null);
  const locations =
    ref.locations ??
    (ref.citations && pageSource
      ? ref.citations.map((c) => citationLocation(c, pageSource))
      : null);
  const noPages = locations?.some((l) => l.kind === 'section') ?? pageSource === 'none';
  return (
    <>
      <CitationGrid
        tone="ai"
        items={[
          { label: 'Source', value: 'Source document' },
          { label: 'Document', value: ref.file_name ?? 'Unknown document', mono: true },
          locations && locations.length > 0
            ? {
                label: locations.length === 1 ? 'Location' : 'Locations',
                value: locations.map(locationWords).join('; '),
              }
            : {
                label: pages.length === 1 ? 'Page' : 'Pages',
                value:
                  pages.length > 0 && pageSource !== 'none'
                    ? pages.join(', ')
                    : pageSource === 'none'
                      ? 'This file does not record pages'
                      : 'Not recorded',
              },
          ...(ref.confidence != null
            ? [{ label: 'Confidence', value: <AiConfidenceChip value={ref.confidence} /> }]
            : []),
        ]}
      />
      <p className="mt-1.5 text-meta text-ink-500">
        Extracted by AI, accepted by {acceptedBy}
        {ref.accepted_at ? ` on ${formatDate(ref.accepted_at)}` : ''}.
        {noPages ? ` ${NO_PAGES_NOTE}` : ''}
      </p>
      {target && onOpenSource && ref.confidence != null ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {locations && locations.length > 0
            ? locations.map((location, i) => (
                <CitationChip
                  key={`location-${i}`}
                  cite={{
                    id: `${row.item_id}:${i}`,
                    doc_label: documentLabel(ref.file_name ?? 'Source'),
                    ...locationChip(location),
                    confidence: ref.confidence ?? 0,
                  }}
                  onOpen={() => onOpenSource({ ...target, citationIndex: i })}
                />
              ))
            : (pages.length > 0 ? pages : [null]).map((page, i) => (
                <CitationChip
                  key={page ?? 'source'}
                  cite={{
                    id: `${row.item_id}:${i}`,
                    doc_label: documentLabel(ref.file_name ?? 'Source'),
                    page,
                    confidence: ref.confidence ?? 0,
                  }}
                  onOpen={() => onOpenSource({ ...target, citationIndex: i })}
                />
              ))}
        </div>
      ) : null}
    </>
  );
}

export function LibraryPanel({
  row,
  module,
  editing,
  busy,
  onClose,
  onCommitText,
  onRetire,
  onRestore,
  onKeepBoth,
  onDiscardDuplicate,
  onOpenHistory,
  onOpenSource,
}: LibraryPanelProps) {
  const published = row.version != null;
  const versionsQuery = useModuleVersionsQuery(
    module && module.current_version > 0 ? module.id : null,
  );
  const usageQuery = useItemUsageQuery(published ? row.item_id : null);
  const current = versionsQuery.data?.versions.find((v) => v.is_current) ?? null;
  const retired = row.change === 'retired';
  const canEdit = editing && !retired;
  const source = sourceItems(row);
  const metadata = metadataEntries(row.metadata);

  return (
    <SidePanel
      aria-label={`Requirement ${row.number}`}
      identifier={row.number}
      path={`${row.module_name} › ${row.subcategory}`}
      onClose={onClose}
      footer={
        editing ? (
          retired ? (
            <WorkspaceButton disabled={busy} onClick={() => onRestore(row)}>
              Keep this requirement
            </WorkspaceButton>
          ) : (
            <WorkspaceButton variant="destructive" disabled={busy} onClick={() => onRetire(row)}>
              {row.change === 'added' ? 'Remove from draft' : 'Retire'}
            </WorkspaceButton>
          )
        ) : (
          <span className="text-meta text-ink-500">
            Engagements bring in requirements from their own Requirements screen.
          </span>
        )
      }
    >
      <PanelSection label="Requirement">
        {canEdit ? (
          <PanelTextEditor
            key={`${row.item_id}:${row.text}`}
            initial={row.text}
            disabled={busy}
            onCommit={(text) => onCommitText(row, text)}
          />
        ) : (
          <p
            className={
              retired
                ? 'text-body leading-[22px] text-ink-300 line-through'
                : 'text-body leading-[22px] text-ink-900'
            }
          >
            {row.text}
          </p>
        )}
      </PanelSection>

      {row.duplicates.length > 0 ? (
        <div className={DIFF_BOX} role="group" aria-label="Possible duplicate">
          <div className="mb-1.5 font-semibold text-ink-900">
            Looks like a requirement already in the library
          </div>
          {row.duplicates.map((d) => (
            <div key={d.item_id} className="mb-2">
              <span className="font-mono text-meta">{d.number}</span>
              <span>
                {' · '}
                {d.module_name}
                {matchKindWords(d.match_kind)
                  ? ` · ${matchKindWords(d.match_kind)}`
                  : d.exact
                    ? ' · same text'
                    : ''}
              </span>
              <span className="block">{d.text}</span>
            </div>
          ))}
          <div className="flex gap-2">
            <WorkspaceButton disabled={busy} onClick={() => onKeepBoth(row)}>
              Keep both
            </WorkspaceButton>
            <WorkspaceButton
              variant="destructive"
              disabled={busy}
              onClick={() => onDiscardDuplicate(row)}
            >
              {row.change === 'reworded' ? 'Undo my rewording' : 'Discard this one'}
            </WorkspaceButton>
          </div>
        </div>
      ) : row.duplicatesKept ? (
        <div className={BOX}>
          Kept alongside a similar requirement. The decision is recorded when this draft is
          published.
        </div>
      ) : null}

      <PanelSection label="Module and sub-category">
        <CitationGrid
          items={[
            { label: 'Category', value: row.category_name },
            { label: 'Module', value: row.module_name },
            { label: 'Sub-category', value: row.subcategory },
          ]}
        />
      </PanelSection>

      {metadata.length > 0 ? (
        <PanelSection label="Metadata">
          <CitationGrid items={metadata.map((m) => ({ label: m.label, value: m.value }))} />
          <p className="mt-1.5 text-meta text-ink-500">
            Kept beside the requirement, not in its wording. Engagements load the wording only.
          </p>
        </PanelSection>
      ) : null}

      <PanelSection label="Source">
        {row.source_kind === 'ai_document' ? (
          <AiDocumentSource row={row} module={module} onOpenSource={onOpenSource} />
        ) : source ? (
          <>
            <CitationGrid items={source.items} />
            <p className="mt-1.5 text-meta text-ink-500">{source.note}</p>
          </>
        ) : (
          <div className={BOX}>Written by a curator in this library.</div>
        )}
      </PanelSection>

      {row.change ? (
        <div className={DIFF_BOX}>
          {row.change === 'retired'
            ? 'Will be retired when this draft is published.'
            : row.change === 'added'
              ? 'New in this draft. Not yet published.'
              : row.change === 'restored'
                ? 'Restored in this draft. Not yet published.'
                : 'Changed in this draft. Not yet published.'}
          {row.change === 'reworded' && row.previous ? (
            <div className="mt-1.5 text-meta text-ink-500">
              Published wording: {row.previous.text}
            </div>
          ) : null}
        </div>
      ) : null}

      <PanelSection label="Version">
        <div className={BOX}>
          {!module || module.current_version < 1 ? (
            'Module not yet published.'
          ) : (
            <>
              {row.module_name}{' '}
              <span className="font-mono text-meta">v{module.current_version}</span>
              {' · published '}
              {formatDate(current?.published_at ?? module.published_at)}
              {current?.published_by.name ? ` by ${current.published_by.name}` : ''}
            </>
          )}
        </div>
      </PanelSection>

      <PanelSection label="Use">
        <div className={BOX}>
          {!published ? (
            'Not yet published, so no engagement has loaded it.'
          ) : usageQuery.isPending ? (
            <Spinner />
          ) : usageQuery.isError ? (
            'The number of engagements could not be loaded.'
          ) : (
            `Loaded into ${plural(usageQuery.data.engagement_count, 'engagement')}.`
          )}
        </div>
      </PanelSection>

      <PanelSection label="Module history">
        {!module || module.current_version < 1 ? (
          <div className={BOX}>No published versions yet.</div>
        ) : versionsQuery.isPending ? (
          <Spinner />
        ) : versionsQuery.isError ? (
          <div className={BOX}>The version history could not be loaded.</div>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {versionsQuery.data.versions.map((v) => (
                <li
                  key={v.version}
                  className="relative pl-3.5 text-meta leading-[18px] text-ink-700 before:absolute before:left-0 before:top-[7px] before:h-[5px] before:w-[5px] before:rounded-full before:bg-ink-300"
                >
                  <button
                    type="button"
                    onClick={() => onOpenHistory(module.id, v.version)}
                    className="rounded-chip font-mono font-medium text-ink-900 underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                    aria-label={`Open version ${v.version}`}
                  >
                    v{v.version}
                  </button>
                  {' — '}
                  {v.change_note || 'No change note'}{' '}
                  <span className="text-ink-500">
                    · {formatDate(v.published_at)}
                    {v.published_by.name ? ` · ${v.published_by.name}` : ''}
                  </span>
                </li>
              ))}
            </ul>
            {versionsQuery.data.versions.length > 1 ? (
              <WorkspaceButton
                className="mt-2.5"
                onClick={() => onOpenHistory(module.id, 'compare')}
              >
                Compare versions
              </WorkspaceButton>
            ) : null}
          </>
        )}
      </PanelSection>
    </SidePanel>
  );
}

/** Uncontrolled textarea that saves on blur when the text changed. */
export function PanelTextEditor({
  initial,
  disabled,
  onCommit,
}: {
  initial: string;
  disabled: boolean;
  onCommit: (text: string) => void;
}) {
  return (
    <textarea
      aria-label="Requirement text"
      defaultValue={initial}
      disabled={disabled}
      onKeyDown={(e) => {
        // Esc here abandons the edit; it must not also close the panel.
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          e.currentTarget.value = initial;
          e.currentTarget.blur();
        }
      }}
      onBlur={(e) => {
        const text = e.currentTarget.value.trim();
        if (text && text !== initial) onCommit(text);
        else e.currentTarget.value = initial;
      }}
      className="block min-h-[120px] w-full resize-y rounded-card border border-ink-200 bg-paper-0 px-2.5 py-2 text-body leading-[22px] text-ink-900 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600 disabled:opacity-60"
    />
  );
}
